import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBrandMode } from "./BrandModeContext";
import { EMPTY_VALUE, fmtMoney } from "./brandUtils";
import { campaignAction } from "./brandDealUtils";
import {
  Avatar,
  BmModal,
  Btn,
  Card,
  Chip,
  InfoButton,
  LifecycleSteps,
  Loading,
  PageHeader,
  StatusBadge,
} from "./ui/BrandUI";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In progress" },
  { key: "completed", label: "Completed" },
];

const matches = (campaign, filter) => {
  if (filter === "all") return true;
  if (filter === "in_progress") return campaign.status === "in_progress" || campaign.status === "in_contract";
  return campaign.status === filter;
};

// "How a campaign moves" lives behind the (i) next to the page title, per
// the design annotation, so it is reachable from anywhere on this page.
export const HowCampaignMovesModal = ({ onClose, current = 1 }) => (
  <BmModal title="How a campaign moves" onClose={onClose} width={1000}>
    <LifecycleSteps current={current} />
  </BmModal>
);

const CampaignsPage = () => {
  const navigate = useNavigate();
  const { campaigns, campaignCounts, campaignsLoading, refreshCampaigns } = useBrandMode();
  const [filter, setFilter] = useState("all");
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    refreshCampaigns();
  }, [refreshCampaigns]);

  const visible = useMemo(() => campaigns.filter((c) => matches(c, filter)), [campaigns, filter]);

  return (
    <div>
      <PageHeader
        title="Campaigns"
        info={<InfoButton onClick={() => setHowOpen(true)} />}
        subtitle="One list from draft to completion. Drafts open in the builder; active campaigns open in deal management."
        actions={<Btn onClick={() => navigate("/brand/campaigns/new")}>New campaign</Btn>}
      />

      <div className="flex flex-col gap-4 pt-7">
        <div className="flex flex-wrap items-center gap-[10px]">
          {FILTERS.map((f) => (
            <Chip key={f.key} active={filter === f.key} count={campaignCounts?.[f.key] ?? 0} onClick={() => setFilter(f.key)}>
              {f.label}
            </Chip>
          ))}
        </div>

        {campaignsLoading && !campaigns.length ? (
          <Loading label="Loading campaigns..." />
        ) : !visible.length ? (
          <Card className="py-16 text-center">
            <p className="bm-body-m text-bm-muted">
              {campaigns.length ? "No campaigns in this state." : "No campaigns yet. Build a shortlist in Discover or start one from scratch."}
            </p>
            {!campaigns.length && (
              <Btn className="mt-4" onClick={() => navigate("/brand/campaigns/new")}>
                New campaign
              </Btn>
            )}
          </Card>
        ) : (
          <Card padding="p-0" className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse">
              <thead>
                <tr>
                  <th className="bm-th !h-11 w-[320px]">Campaign</th>
                  <th className="bm-th !h-11 w-[150px]">Status</th>
                  <th className="bm-th !h-11 w-[170px]">Athletes</th>
                  <th className="bm-th !h-11 w-[150px] !text-center">Budget</th>
                  <th className="bm-th !h-11 w-[150px] !text-center">Paid</th>
                  <th className="bm-th !h-11" />
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const action = campaignAction(c);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => navigate(action.href)}
                      className="h-[68px] cursor-pointer hover:bg-bm-subtle"
                    >
                      <td className="bm-td">
                        <span className="flex items-center gap-[10px]">
                          <Avatar initials={c.initials} size={40} tone="dark" square />
                          <span className="flex min-w-0 flex-col gap-[2px]">
                            <span className="truncate bm-h-xs text-bm-ink">{c.name}</span>
                            <span className="bm-body-s text-bm-muted">
                              {c.status === "draft" ? "Not sent yet" : c.dateRange || "No dates set"}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="bm-td">
                        <StatusBadge status={c.status} kind="campaign" />
                      </td>
                      <td className="bm-td bm-body-m text-bm-ink">{c.athletesText}</td>
                      <td className="bm-td text-center bm-num-m text-bm-ink">
                        {c.budget?.total ? fmtMoney(c.budget.total) : <span className="bm-body-m text-bm-tertiary">{EMPTY_VALUE}</span>}
                      </td>
                      <td className="bm-td text-center bm-body-m text-bm-ink">
                        {c.status === "draft" ? <span className="text-bm-tertiary">{EMPTY_VALUE}</span> : fmtMoney(c.paid || 0)}
                      </td>
                      <td className="bm-td text-right">
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(action.href);
                          }}
                        >
                          {action.label}
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {howOpen && <HowCampaignMovesModal onClose={() => setHowOpen(false)} />}
    </div>
  );
};

export default CampaignsPage;
